import {
  ArchiveProgramInputSchema,
  CampActionResultSchema,
  CampImportCrosswalkDetailSchema,
  CampImportCrosswalkQueueResponseSchema,
  CampProgramEditorResponseSchema,
  CampProgramSchema,
  CampRegistrationDraftSchema,
  CampRegistrationListResponseSchema,
  CampRegistrationSchema,
  CampRosterExportRecordSchema,
  CampSessionSchema,
  CampWaitlistEntrySchema,
  CampWaitlistListResponseSchema,
  CancelRegistrationInputSchema,
  ClearCampCrosswalkLinkInputSchema,
  ConfirmCampShadowFamilyLinkInputSchema,
  ConfirmCampShadowStudentLinkInputSchema,
  CreateProgramInputSchema,
  GetCampImportCrosswalkDetailInputSchema,
  GetParentCatalogInputSchema,
  GetParentRegistrationInputSchema,
  GetProgramEditorInputSchema,
  GetProgramInputSchema,
  GetSchoolDashboardInputSchema,
  IssueRefundInputSchema,
  JoinWaitlistInputSchema,
  ListCampImportCrosswalkQueueInputSchema,
  LeaveWaitlistInputSchema,
  ListParentRegistrationsInputSchema,
  ListParentWaitlistInputSchema,
  ManualEnrollCamperInputSchema,
  ParentCampCatalogResponseSchema,
  ParentCampRegistrationResponseSchema,
  PromoteWaitlistEntryInputSchema,
  PublishProgramInputSchema,
  RecomputeCampCrosswalkCandidatesInputSchema,
  RejectCampShadowFamilyCandidatesInputSchema,
  RejectCampShadowStudentCandidatesInputSchema,
  RequestRosterExportInputSchema,
  SaveRegistrationDraftInputSchema,
  SchoolCampDashboardResponseSchema,
  SchoolCampProgramDetailResponseSchema,
  SubmitRegistrationInputSchema,
  TriggerCampCommunicationInputSchema,
  UpdateProgramInputSchema,
  UpdateSessionCapacityInputSchema,
} from '@triedge/domain-core';
import {
  toAppSyncMutationName,
  toAppSyncOperationName,
  toRtkGlueModuleId,
  toSemanticRouteId,
  type AppSyncMutationName,
  type AppSyncOperationName,
  type RtkGlueModuleId,
  type SemanticRouteId,
} from './semantic-types';

type CampsQueryContract = {
  operationName: AppSyncOperationName;
  routeId: SemanticRouteId;
  inputSchema?: unknown;
  outputSchema: unknown;
};

type CampsMutationContract = {
  mutationName: AppSyncMutationName;
  routeId: SemanticRouteId;
  inputSchema: unknown;
  outputSchema: unknown;
};

export type CampsQueryContractName =
  | 'getParentCatalog'
  | 'getParentRegistration'
  | 'listParentWaitlist'
  | 'listParentRegistrations'
  | 'getSchoolDashboard'
  | 'getProgram'
  | 'getProgramEditor'
  | 'listCampImportCrosswalkQueue'
  | 'getCampImportCrosswalkDetail';

export type CampsMutationContractName =
  | 'saveRegistrationDraft'
  | 'submitRegistration'
  | 'cancelRegistration'
  | 'joinWaitlist'
  | 'leaveWaitlist'
  | 'createProgram'
  | 'updateProgram'
  | 'publishProgram'
  | 'archiveProgram'
  | 'updateSessionCapacity'
  | 'promoteWaitlistEntry'
  | 'manualEnrollCamper'
  | 'requestRosterExport'
  | 'issueRefund'
  | 'triggerCampCommunication'
  | 'confirmCampShadowFamilyLink'
  | 'confirmCampShadowStudentLink'
  | 'rejectCampShadowFamilyCandidates'
  | 'rejectCampShadowStudentCandidates'
  | 'clearCampCrosswalkLink'
  | 'recomputeCampCrosswalkCandidates';

export const CAMPS_MODULE_ID: RtkGlueModuleId = toRtkGlueModuleId('camps');

export const CAMPS_ROUTE_IDS = Object.freeze({
  parentCatalog: toSemanticRouteId('camps-parent-catalog'),
  parentRegister: toSemanticRouteId('camps-parent-register'),
  parentWaitlist: toSemanticRouteId('camps-parent-waitlist'),
  parentRegistrations: toSemanticRouteId('camps-parent-registrations'),
  schoolDashboard: toSemanticRouteId('camps-school-dashboard'),
  schoolNew: toSemanticRouteId('camps-school-new'),
  schoolDetail: toSemanticRouteId('camps-school-detail'),
});

const campsQueryContracts: Record<CampsQueryContractName, CampsQueryContract> = {
  getParentCatalog: {
    operationName: toAppSyncOperationName('getParentCatalog'),
    routeId: CAMPS_ROUTE_IDS.parentCatalog,
    inputSchema: GetParentCatalogInputSchema,
    outputSchema: ParentCampCatalogResponseSchema,
  },
  getParentRegistration: {
    operationName: toAppSyncOperationName('getParentRegistration'),
    routeId: CAMPS_ROUTE_IDS.parentRegister,
    inputSchema: GetParentRegistrationInputSchema,
    outputSchema: ParentCampRegistrationResponseSchema,
  },
  listParentWaitlist: {
    operationName: toAppSyncOperationName('listParentWaitlist'),
    routeId: CAMPS_ROUTE_IDS.parentWaitlist,
    inputSchema: ListParentWaitlistInputSchema,
    outputSchema: CampWaitlistListResponseSchema,
  },
  listParentRegistrations: {
    operationName: toAppSyncOperationName('listParentRegistrations'),
    routeId: CAMPS_ROUTE_IDS.parentRegistrations,
    inputSchema: ListParentRegistrationsInputSchema,
    outputSchema: CampRegistrationListResponseSchema,
  },
  getSchoolDashboard: {
    operationName: toAppSyncOperationName('getSchoolDashboard'),
    routeId: CAMPS_ROUTE_IDS.schoolDashboard,
    inputSchema: GetSchoolDashboardInputSchema,
    outputSchema: SchoolCampDashboardResponseSchema,
  },
  getProgram: {
    operationName: toAppSyncOperationName('getProgram'),
    routeId: CAMPS_ROUTE_IDS.schoolDetail,
    inputSchema: GetProgramInputSchema,
    outputSchema: SchoolCampProgramDetailResponseSchema,
  },
  getProgramEditor: {
    operationName: toAppSyncOperationName('getProgramEditor'),
    routeId: CAMPS_ROUTE_IDS.schoolNew,
    inputSchema: GetProgramEditorInputSchema,
    outputSchema: CampProgramEditorResponseSchema,
  },
  listCampImportCrosswalkQueue: {
    operationName: toAppSyncOperationName('listCampImportCrosswalkQueue'),
    routeId: CAMPS_ROUTE_IDS.schoolDashboard,
    inputSchema: ListCampImportCrosswalkQueueInputSchema,
    outputSchema: CampImportCrosswalkQueueResponseSchema,
  },
  getCampImportCrosswalkDetail: {
    operationName: toAppSyncOperationName('getCampImportCrosswalkDetail'),
    routeId: CAMPS_ROUTE_IDS.schoolDetail,
    inputSchema: GetCampImportCrosswalkDetailInputSchema,
    outputSchema: CampImportCrosswalkDetailSchema,
  },
};

export const CAMPS_QUERY_CONTRACTS: Readonly<
  Record<CampsQueryContractName, CampsQueryContract>
> = Object.freeze(campsQueryContracts);

const campsMutationContracts: Record<
  CampsMutationContractName,
  CampsMutationContract
> = {
  saveRegistrationDraft: {
    mutationName: toAppSyncMutationName('saveRegistrationDraft'),
    routeId: CAMPS_ROUTE_IDS.parentRegister,
    inputSchema: SaveRegistrationDraftInputSchema,
    outputSchema: CampRegistrationDraftSchema,
  },
  submitRegistration: {
    mutationName: toAppSyncMutationName('submitRegistration'),
    routeId: CAMPS_ROUTE_IDS.parentRegister,
    inputSchema: SubmitRegistrationInputSchema,
    outputSchema: CampRegistrationListResponseSchema,
  },
  cancelRegistration: {
    mutationName: toAppSyncMutationName('cancelRegistration'),
    routeId: CAMPS_ROUTE_IDS.parentRegistrations,
    inputSchema: CancelRegistrationInputSchema,
    outputSchema: CampRegistrationSchema,
  },
  joinWaitlist: {
    mutationName: toAppSyncMutationName('joinWaitlist'),
    routeId: CAMPS_ROUTE_IDS.parentRegister,
    inputSchema: JoinWaitlistInputSchema,
    outputSchema: CampWaitlistEntrySchema,
  },
  leaveWaitlist: {
    mutationName: toAppSyncMutationName('leaveWaitlist'),
    routeId: CAMPS_ROUTE_IDS.parentWaitlist,
    inputSchema: LeaveWaitlistInputSchema,
    outputSchema: CampWaitlistEntrySchema,
  },
  createProgram: {
    mutationName: toAppSyncMutationName('createProgram'),
    routeId: CAMPS_ROUTE_IDS.schoolNew,
    inputSchema: CreateProgramInputSchema,
    outputSchema: CampProgramEditorResponseSchema,
  },
  updateProgram: {
    mutationName: toAppSyncMutationName('updateProgram'),
    routeId: CAMPS_ROUTE_IDS.schoolDetail,
    inputSchema: UpdateProgramInputSchema,
    outputSchema: CampProgramEditorResponseSchema,
  },
  publishProgram: {
    mutationName: toAppSyncMutationName('publishProgram'),
    routeId: CAMPS_ROUTE_IDS.schoolDetail,
    inputSchema: PublishProgramInputSchema,
    outputSchema: CampProgramSchema,
  },
  archiveProgram: {
    mutationName: toAppSyncMutationName('archiveProgram'),
    routeId: CAMPS_ROUTE_IDS.schoolDetail,
    inputSchema: ArchiveProgramInputSchema,
    outputSchema: CampProgramSchema,
  },
  updateSessionCapacity: {
    mutationName: toAppSyncMutationName('updateSessionCapacity'),
    routeId: CAMPS_ROUTE_IDS.schoolDetail,
    inputSchema: UpdateSessionCapacityInputSchema,
    outputSchema: CampSessionSchema,
  },
  promoteWaitlistEntry: {
    mutationName: toAppSyncMutationName('promoteWaitlistEntry'),
    routeId: CAMPS_ROUTE_IDS.schoolDetail,
    inputSchema: PromoteWaitlistEntryInputSchema,
    outputSchema: CampRegistrationSchema,
  },
  manualEnrollCamper: {
    mutationName: toAppSyncMutationName('manualEnrollCamper'),
    routeId: CAMPS_ROUTE_IDS.schoolDetail,
    inputSchema: ManualEnrollCamperInputSchema,
    outputSchema: CampRegistrationSchema,
  },
  requestRosterExport: {
    mutationName: toAppSyncMutationName('requestRosterExport'),
    routeId: CAMPS_ROUTE_IDS.schoolDetail,
    inputSchema: RequestRosterExportInputSchema,
    outputSchema: CampRosterExportRecordSchema,
  },
  issueRefund: {
    mutationName: toAppSyncMutationName('issueRefund'),
    routeId: CAMPS_ROUTE_IDS.schoolDetail,
    inputSchema: IssueRefundInputSchema,
    outputSchema: CampRegistrationSchema,
  },
  triggerCampCommunication: {
    mutationName: toAppSyncMutationName('triggerCampCommunication'),
    routeId: CAMPS_ROUTE_IDS.schoolDetail,
    inputSchema: TriggerCampCommunicationInputSchema,
    outputSchema: CampActionResultSchema,
  },
  confirmCampShadowFamilyLink: {
    mutationName: toAppSyncMutationName('confirmCampShadowFamilyLink'),
    routeId: CAMPS_ROUTE_IDS.schoolDashboard,
    inputSchema: ConfirmCampShadowFamilyLinkInputSchema,
    outputSchema: CampImportCrosswalkDetailSchema,
  },
  confirmCampShadowStudentLink: {
    mutationName: toAppSyncMutationName('confirmCampShadowStudentLink'),
    routeId: CAMPS_ROUTE_IDS.schoolDetail,
    inputSchema: ConfirmCampShadowStudentLinkInputSchema,
    outputSchema: CampImportCrosswalkDetailSchema,
  },
  rejectCampShadowFamilyCandidates: {
    mutationName: toAppSyncMutationName('rejectCampShadowFamilyCandidates'),
    routeId: CAMPS_ROUTE_IDS.schoolDashboard,
    inputSchema: RejectCampShadowFamilyCandidatesInputSchema,
    outputSchema: CampImportCrosswalkDetailSchema,
  },
  rejectCampShadowStudentCandidates: {
    mutationName: toAppSyncMutationName('rejectCampShadowStudentCandidates'),
    routeId: CAMPS_ROUTE_IDS.schoolDetail,
    inputSchema: RejectCampShadowStudentCandidatesInputSchema,
    outputSchema: CampImportCrosswalkDetailSchema,
  },
  clearCampCrosswalkLink: {
    mutationName: toAppSyncMutationName('clearCampCrosswalkLink'),
    routeId: CAMPS_ROUTE_IDS.schoolDetail,
    inputSchema: ClearCampCrosswalkLinkInputSchema,
    outputSchema: CampImportCrosswalkDetailSchema,
  },
  recomputeCampCrosswalkCandidates: {
    mutationName: toAppSyncMutationName('recomputeCampCrosswalkCandidates'),
    routeId: CAMPS_ROUTE_IDS.schoolDetail,
    inputSchema: RecomputeCampCrosswalkCandidatesInputSchema,
    outputSchema: CampImportCrosswalkDetailSchema,
  },
};

export const CAMPS_MUTATION_CONTRACTS: Readonly<
  Record<CampsMutationContractName, CampsMutationContract>
> = Object.freeze(campsMutationContracts);
